import React, { useState } from 'react'
import { useFindMenu, useTrans } from '@next-nx/hooks'
import { WContainer, Breadcrumbs, MainTitle, MainDescription } from '@next-nx/shared-ui/atoms'
import { CircleBox, ContattaciBox, InfoBox } from '@next-nx/shared-ui/molecules'
import {
  FaqAssistenzaSection,
  FormRichiestaPreventivoModal,
  Page,
  RichiestaPreventivoThankYouModal,
  StoreLocatorHelpBanner,
} from '@next-nx/shared-ui/organisms'
import styles from './AssistenzaClientiTemplate.module.sass'
import { useCreateRichiestaContattoMutation } from '@next-nx/shared-graphql/unipolrental'

interface Props {
  pagina?: any
}

const AssistenzaClientiTemplate = (props: Props) => {
  const { pagina = {} } = props

  const t = useTrans()

  const [openFormRichiestaPreventivoModal, setOpenFormRichiestaPreventivoModal] = useState(false)
  const [openRichiestaPreventivoThankYouModal, setOpenRichiestaPreventivoThankYouModal] =
    useState(false)
  const [statusRichiestaPreventivoThankYouModal, setStatusRichiestaPreventivoThankYouModal] =
    useState<string | undefined>()

  const [createRichiestaContattoMutation] = useCreateRichiestaContattoMutation()

  const assistenzaClientiMenu = useFindMenu('pagina-assistenzaclienti')

  const target = {
    children: {
      edges: [
        {
          node: {
            pk: 1,
            name: 'Sei un fleet manager?',
            description:
              '<p>Accedi per gestire la tua flotta: visualizza i documenti, i dati relativi ai veicoli, la reportistica, gestisci i driver.</p>',
            children: {
              edges: [
                {
                  node: {
                    pk: 1,
                    name: 'Accedi all’area riservata',
                    url: 'https://unipolrental.link/areaclienti',
                    key: 'area_riservata',
                    blank: true,
                  },
                },
              ],
            },
          },
        },
        {
          node: {
            pk: 2,
            name: 'Sei un driver?',
            description: '<p>Accedi per visualizzare i documenti e i dati relativi al veicolo.</p>',
            children: {
              edges: [
                {
                  node: {
                    pk: 1,
                    name: 'Accedi all’area riservata',
                    url: 'https://unipolrental.link/areaclienti',
                    key: 'area_riservata',
                    blank: true,
                  },
                },
              ],
            },
          },
        },
        {
          node: {
            pk: 3,
            name: 'Sei un cliente?',
            description:
              '<p>Accedi per visualizzare i documenti, i dati relativi al veicolo, la reportistica.</p>',
            children: {
              edges: [
                {
                  node: {
                    pk: 1,
                    name: 'Accedi all’area riservata',
                    url: 'https://unipolrental.link/areaclienti',
                    key: 'area_riservata',
                    blank: true,
                  },
                },
              ],
            },
          },
        },
      ],
    },
  }

  const infoText =
    '<p>Nella tua <strong>Area Riservata</strong> troverai inoltre risposte alle domande frequenti inerenti il tuo contratto di noleggio, in particolare: assicurazione, carburante, contratto, fatturazione, manutenzione, multe, pneumatici, sinistri, supporto clienti.</p>'

  const contattaciList = {
    children: {
      edges: [
        {
          node: {
            pk: 1,
            key: 'numero_verde',
            style: 'assistenza_light_illustration',
            name: 'Hai bisogno del soccorso stradale?',
            description:
              '<p>Contatta il nostro numero verde gratuito per ricevere soccorso stradale.</p>',
            children: {
              edges: [
                {
                  node: {
                    pk: 11,
                    name: 'Chiama',
                    url: `tel:${t('numero_verde')}`,
                    style: 'phone',
                  },
                },
              ],
            },
          },
        },
        {
          node: {
            pk: 2,
            style: 'preventivo_illustration',
            name: 'Hai bisogno di un preventivo su un veicolo?',
            description:
              '<p>Compila il form di richiesta preventivo per essere ricontattato da un nostro consulente.</p>',
            children: {
              edges: [
                {
                  node: {
                    pk: 21,
                    name: 'Richiedi un preventivo',
                    key: 'richiesta_preventivo',
                    url: '/',
                  },
                },
                {
                  node: {
                    pk: 22,
                    name: 'Oppure vai in Agenzia',
                    url: '/agenzie',
                  },
                },
              ],
            },
          },
        },
      ],
    },
  }

  const faqsMenu = useFindMenu('faq', assistenzaClientiMenu)

  const faqBoxLink = {
    name: 'Hai dubbi sul noleggio a lungo termine?',
    ctaLabel: 'Vai alle FAQ',
    url: '/faq',
  }

  const circleBox1Menu = useFindMenu('box1', assistenzaClientiMenu)
  const circleBox2Menu = useFindMenu('box2', assistenzaClientiMenu)

  const helpBannerMenu = useFindMenu('help_banner')
  const helpBannerAssistenzaMenu = useFindMenu('help-banner-assistenzaclienti', helpBannerMenu)

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} className={styles.breadcrumbs} />
        <MainTitle label={pagina.titolo} className={styles.title} />
        <MainDescription html={pagina.descrizione} className={styles.descrizione} />
        {!!target?.children?.edges?.length && (
          <div className={styles.target}>
            {target.children.edges
              .map((x) => x.node)
              .map((x) => (
                <ContattaciBox item={x} key={x.pk} />
              ))}
          </div>
        )}
        <InfoBox text={infoText} className={styles.info} />
      </WContainer>
      {contattaciList?.children?.edges?.length == 2 && (
        <div className={styles.contatti}>
          <div className={styles.contattiBg}>
            <WContainer>
              <div className={styles.contattiList}>
                {contattaciList.children.edges
                  .map((x) => x.node)
                  .map((x, i) => (
                    <div className={styles.contattiItem} key={x.pk}>
                      <ContattaciBox
                        item={x}
                        colorVariant={i % 2 == 0 ? 'white' : ''}
                        numeroVerde={x.key === 'numero_verde'}
                        numeroVerdeNoBackground
                        onClick={
                          x.children.edges.some((n) => n.node?.key === 'richiesta_preventivo')
                            ? () => setOpenFormRichiestaPreventivoModal(true)
                            : undefined
                        }
                      />
                    </div>
                  ))}
              </div>
            </WContainer>
          </div>
        </div>
      )}
      {!!faqsMenu && (
        <FaqAssistenzaSection item={faqsMenu} boxLinkItem={faqBoxLink} className={styles.faq} />
      )}
      {(circleBox1Menu || circleBox2Menu) && (
        <WContainer>
          <div className={styles.boxLinks}>
            {circleBox1Menu && <CircleBox item={circleBox1Menu} />}
            {circleBox2Menu && <CircleBox item={circleBox2Menu} animationVariant="horizontal" />}
          </div>
        </WContainer>
      )}
      {helpBannerAssistenzaMenu && (
        <StoreLocatorHelpBanner
          item={helpBannerAssistenzaMenu}
          boxLinkItem={helpBannerAssistenzaMenu.children?.edges?.[0]?.node}
          className={styles.storeLocatorBox}
        />
      )}

      <FormRichiestaPreventivoModal
        open={openFormRichiestaPreventivoModal}
        setOpen={setOpenFormRichiestaPreventivoModal}
        mutation={createRichiestaContattoMutation}
        onSubmit={(status) => {
          setOpenFormRichiestaPreventivoModal(false)
          setOpenRichiestaPreventivoThankYouModal(true)
          setStatusRichiestaPreventivoThankYouModal(status)
        }}
      />
      <RichiestaPreventivoThankYouModal
        open={openRichiestaPreventivoThankYouModal}
        setOpen={setOpenRichiestaPreventivoThankYouModal}
        status={statusRichiestaPreventivoThankYouModal}
      />
    </Page>
  )
}

export default AssistenzaClientiTemplate
